import "@/App.css";
import { useLayoutEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { LanguageProvider } from "@/context/LanguageContext";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import WhatIBuild from "@/components/WhatIBuild";
import SelectedWork from "@/components/SelectedWork";
import SaaSExplainers from "@/components/SaaSExplainers";
import Resources from "@/components/Resources";
import FAQ from "@/components/FAQ";
import Contact from "@/components/Contact";
import Footer from "@/components/Footer";
import AdminLogin from "@/admin/AdminLogin";
import AdminResources from "@/admin/AdminResources";
import AdminProjects from "@/admin/AdminProjects";
import AdminExplainers from "@/admin/AdminExplainers";
import ProjectDetail from "@/components/ProjectDetail";
import { ContactFormProvider } from "@/context/ContactFormContext";
import ContactFormDrawer from "@/components/ContactFormDrawer";
import HowWeWork from "@/components/HowWeWork";

const HashSectionScroll = () => {
  useLayoutEffect(() => {
    let stopTracking;

    const positionAtHash = () => {
      const id = decodeURIComponent(window.location.hash.slice(1));
      const section = id && document.getElementById(id);
      if (!section) return;
      const margin = parseFloat(window.getComputedStyle(section).scrollMarginTop) || 0;
      const top = Math.max(0, section.getBoundingClientRect().top + window.scrollY - margin);
      if (Math.abs(window.scrollY - top) > 2) window.scrollTo({ top, behavior: "instant" });
    };

    const startTracking = () => {
      stopTracking?.();
      positionAtHash();
      const id = decodeURIComponent(window.location.hash.slice(1));
      if (!id) return;
      const section = document.getElementById(id);

      const observer = new ResizeObserver(positionAtHash);
      if (section) {
        const sections = Array.from(document.querySelectorAll("main > section"));
        for (const item of sections) {
          observer.observe(item);
          if (item === section) break;
        }
      }
      const frame = window.requestAnimationFrame(positionAtHash);
      const interval = window.setInterval(positionAtHash, 150);
      const timeout = window.setTimeout(() => stopTracking?.(), 5000);
      const cancel = () => stopTracking?.();
      for (const event of ["wheel", "touchstart", "pointerdown", "keydown"]) {
        window.addEventListener(event, cancel, { once: true, passive: true });
      }
      stopTracking = () => {
        observer.disconnect();
        window.cancelAnimationFrame(frame);
        window.clearInterval(interval);
        window.clearTimeout(timeout);
        for (const event of ["wheel", "touchstart", "pointerdown", "keydown"]) window.removeEventListener(event, cancel);
        stopTracking = null;
      };
    };

    startTracking();
    window.addEventListener("hashchange", startTracking);
    window.addEventListener("pageshow", startTracking);
    window.addEventListener("load", positionAtHash);
    return () => {
      stopTracking?.();
      window.removeEventListener("hashchange", startTracking);
      window.removeEventListener("pageshow", startTracking);
      window.removeEventListener("load", positionAtHash);
    };
  }, []);
  return null;
};

const HomePage = () => (
  <LanguageProvider>
    <ContactFormProvider>
      <HashSectionScroll />
      <div className="site-shell min-h-screen bg-softwhite text-ink">
        <Header />
        <main className="site-main">
          <Hero />
          <WhatIBuild />
          <SelectedWork />
          <SaaSExplainers />
          <HowWeWork />
          <FAQ />
          <Contact />
          <Resources />
        </main>
        <Footer />
        <Toaster position="bottom-center" />
      </div>
      <ContactFormDrawer />
    </ContactFormProvider>
  </LanguageProvider>
);

const AdminPage = ({ children }) => <LanguageProvider>{children}</LanguageProvider>;

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/admin/login" element={<AdminPage><AdminLogin /></AdminPage>} />
        <Route path="/admin/resources" element={<AdminPage><AdminResources /></AdminPage>} />
        <Route path="/admin/projects" element={<AdminPage><AdminProjects /></AdminPage>} />
        <Route path="/admin/saas-explainers" element={<AdminPage><AdminExplainers /></AdminPage>} />
        <Route path="/projects/:slug" element={<AdminPage><ProjectDetail /></AdminPage>} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
